import { Box, Grid2, Typography } from "@mui/material";
import QuickFilter from "./Components/QuickFilter";
import AlertListFilters from "./AlertListFilters";
import { useEffect, useState } from "react";
import { getPagesMetaData } from "../../Api/Api";
import { fetchFiltersDataObject } from "../../Helper/Table/TableHelper";
import { usePagesContent } from "../../Hooks/usePagesContent";

const AlertListFiltersSelection = ({
  selectedFilters,
  onApplyFilters,
  selectedTab,
  filterConfigKey = "diagnosticsStateTable",
  alertStatusKey = "bmsAlertStatus",
}) => {
  const [filters, setFilters] = useState({});
  const [highlightQuickFilters, setHighlightQuickFilters] = useState({});

  const pageContent = usePagesContent();

  const handleFiltersSelection = (filters) => {
    onApplyFilters(filters);
  };

  function handleQuickFilter(filterType, filterValue) {
    const updatedFilters = { ...selectedFilters };

    // ensure batteryStatus array exists
    if (!Array.isArray(updatedFilters[filterType])) {
      updatedFilters[filterType] = [];
    }

    if (updatedFilters[filterType].includes(filterValue)) {
      // remove it
      updatedFilters[filterType] = updatedFilters[filterType].filter(
        (v) => v !== filterValue
      );
      // if empty, remove the key
      if (updatedFilters[filterType].length === 0) {
        delete updatedFilters[filterType];
      }
    } else {
      // add it
      updatedFilters[filterType] = [...updatedFilters[filterType], filterValue];
    }
    onApplyFilters(updatedFilters);
  }

  const fetchFiltersData = async () => {
    const alertFilters = pageContent?.metaInfo?.filterConfigurations?.[filterConfigKey]?.filters;
    if (!alertFilters) {
      return false;
    }

    const tabvalue = 0;
    const tabFilters = alertFilters.filter((filter, index) => {
      if (!filter.tabs) {
        return true;
      }
      return filter.tabs.includes(tabvalue);
    });

    const filtersObj = await fetchFiltersDataObject(tabFilters, filters);

    if (filtersObj?.hierarchy?.values && !filtersObj.hierarchy.values.some(v => v.id === 'tms')) {
      filtersObj.hierarchy.values.push({ id: 'tms', value: 'TMS' });
    }

    const resolvedStatuses = ['Resolved', 'Auto Resolved', 'Force Resolved'];

    // Filter for active tab: exclude resolved statuses
    const activeFiltersObj = { ...filtersObj };
    if (activeFiltersObj?.[alertStatusKey]?.values) {
      activeFiltersObj[alertStatusKey] = {
        ...activeFiltersObj[alertStatusKey],
        values: activeFiltersObj[alertStatusKey].values.filter(
          option => !resolvedStatuses.includes(option.value)
        )
      };
    }

    const activeSortedFiltersObj = Object.fromEntries(
      Object.entries(activeFiltersObj)
        .filter(([, a]) => a.label)
        .sort(([, a], [, b]) => a.label.localeCompare(b.label))
    );

    // Filter for resolved tab: include only resolved statuses
    const resolvedFiltersObj = { ...filtersObj };
    if (resolvedFiltersObj?.[alertStatusKey]?.values) {
      resolvedFiltersObj[alertStatusKey] = {
        ...resolvedFiltersObj[alertStatusKey],
        values: resolvedFiltersObj[alertStatusKey].values.filter(
          option => resolvedStatuses.includes(option.value)
        )
      };
    }

    const resolvedSortedFiltersObj = Object.fromEntries(
      Object.entries(resolvedFiltersObj)
        .filter(([, a]) => a.label)
        .sort(([, a], [, b]) => a.label.localeCompare(b.label))
    );

    setFilters({
      'active': activeSortedFiltersObj,
      'resolved': resolvedSortedFiltersObj
    });
  };

  useEffect(() => {
    fetchFiltersData();
  }, [pageContent]);

  useEffect(() => {
    const severityNameFilters = (selectedFilters?.severityName || []).map(option => option.trim().toLowerCase());
    const bmsAlertStatusFilters = (selectedFilters?.[alertStatusKey] || []).map(option => option.trim().toLowerCase());
    setHighlightQuickFilters({
      critical: severityNameFilters.includes('critical'),
      highSeverity: severityNameFilters.includes('high severity'),
      warning: severityNameFilters.includes('warning'),
      open: bmsAlertStatusFilters.includes('open'),
      inProgress: bmsAlertStatusFilters.includes('in progress'),
      autoResolved: bmsAlertStatusFilters.includes('auto resolved'),
    });
  }, [selectedFilters]);

  return (
    <Grid2 container gap={1.5}>
      <AlertListFilters
        filters={filters[selectedTab]}
        selectedFilters={selectedFilters}
        setSelectedFilters={handleFiltersSelection}
      />
      <Grid2 container gap={0.5}>
        <QuickFilter
          highlighted={highlightQuickFilters?.critical}
          label={"Critical"}
          onClick={() => handleQuickFilter('severityName', 'Critical')}
        />
        <QuickFilter
          highlighted={highlightQuickFilters?.highSeverity}
          label={"High Severity"}
          onClick={() => handleQuickFilter('severityName', 'High Severity')}
        />
        <QuickFilter
          highlighted={highlightQuickFilters?.warning}
          label={"Warning"}
          onClick={() => handleQuickFilter('severityName', 'Warning')}
        />
        {selectedTab === 'active' && (
          <>
            <QuickFilter
              highlighted={highlightQuickFilters?.open}
              label={"Open"}
              onClick={() => handleQuickFilter(alertStatusKey, 'Open')}
            />
            <QuickFilter
              highlighted={highlightQuickFilters?.inProgress}
              label={"In Progress"}
              onClick={() => handleQuickFilter(alertStatusKey, 'In Progress')}
            />
          </>
        )}
        {selectedTab === 'resolved' && (
          <>
            <QuickFilter
              highlighted={highlightQuickFilters?.autoResolved}
              label={"Auto Resolved"}
              onClick={() => handleQuickFilter(alertStatusKey, 'Auto Resolved')}
            />
          </>
        )}
      </Grid2>
    </Grid2>
  );
};

export default AlertListFiltersSelection;
